import { ReactNode, useMemo, useState } from 'react';

import {
  ColumnDef,
  SortingState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
} from '@tanstack/react-table';

import { ScrollableTable } from './table/scrollable-table';
import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { ADMINISTRATOR_ROLE_NAME, SEAT_BASED_ROLE_NAME } from '../../../../backend/src/user/roles/user-roles-defaults';
import { Role } from '@/api/queries/useGetRoles';
import { User } from '@/api/types/backend-dto-types';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

interface MatterUserRow {
  id: string;
  email: string;
  globalRoleName: string;
  roleOverrideName: string;
  isCurrentUser: boolean;
  isCreator: boolean;
  isAdmin: boolean;
  override?: { id: string; roleId: string; roleName: string };
}

interface MatterUsersTableProps {
  matterUsers: MatterUserRow[];
  overridesEnabled: boolean;
  canManageOverrides: boolean;
  canRemoveUsers: boolean;
  roles: Role[];
  additionalButtons?: ReactNode;
  onRoleOverrideChange: (userId: string, roleId: string) => void;
  onRemoveUser: (userId: string) => void;
}

const columnHelper = createColumnHelper<MatterUserRow>();

export function MatterUsersTable({
  matterUsers,
  overridesEnabled,
  canManageOverrides,
  canRemoveUsers,
  roles,
  additionalButtons,
  onRoleOverrideChange,
  onRemoveUser,
}: MatterUsersTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Deduplicate roles by name, keeping the first occurrence (matching users-table pattern)
  const uniqueRoles = useMemo(() => {
    const seen = new Set<string>();
    return roles.filter((r) => {
      if (seen.has(r.name)) return false;
      seen.add(r.name);
      return true;
    });
  }, [roles]);

  const hasSeatBasedUsers = useMemo(
    () => matterUsers.some((u) => u.globalRoleName === SEAT_BASED_ROLE_NAME),
    [matterUsers],
  );

  const columns = useMemo(() => {
    const cols: ColumnDef<MatterUserRow, any>[] = [
      columnHelper.accessor('email', {
        header: 'Email',
        cell: ({ row }) => (
          <div className="flex items-center gap-2 lowercase">
            {row.original.email}
            {row.original.isCurrentUser && <span className="text-xs text-gray-500">(You)</span>}
            {row.original.isCreator && <span className="text-xs text-gray-500">(Creator)</span>}
          </div>
        ),
      }),
      columnHelper.accessor('globalRoleName', {
        header: 'Global Role',
        cell: ({ row }) => (
          <span className={`text-sm ${overridesEnabled && row.original.override ? 'text-gray-400' : 'text-gray-700'}`}>
            {row.original.globalRoleName}
          </span>
        ),
      }),
    ];

    if (overridesEnabled) {
      cols.push(
        columnHelper.accessor('roleOverrideName', {
          header: 'Role Override',
          cell: ({ row }) => {
            const { isAdmin, override } = row.original;
            // Org-level admins (global Administrator role) can never be demoted
            // within a matter. Everyone else (including the creator) can be
            // re-assigned by a manager; the backend still blocks demoting the
            // last matter admin.
            const canOverride = !isAdmin;

            if (canOverride && canManageOverrides) {
              return (
                <Select
                  value={override?.roleId ?? 'none'}
                  onValueChange={(roleId: string) => onRoleOverrideChange(row.original.id, roleId)}
                >
                  <SelectTrigger className="-m-0.5 h-auto w-40 border-none p-1 pl-2 focus:outline-none">
                    <span className={!override ? 'text-gray-400' : ''}>
                      <SelectValue placeholder="No override" />
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No override</SelectItem>
                    {uniqueRoles
                      .filter((r) => r.name !== SEAT_BASED_ROLE_NAME || hasSeatBasedUsers)
                      .map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          {role.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              );
            }

            if (isAdmin) {
              const reason = 'Organisation administrators cannot be demoted within a Matter.';
              return (
                <div className="flex items-center gap-1.5 px-0.5 py-0.5 pl-1.5 text-gray-500">
                  {override?.roleName ?? ADMINISTRATOR_ROLE_NAME}
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <button className="shrink-0">
                        <Icon className="lucide--info h-3 w-3 text-gray-400" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p className="text-xs">{reason}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              );
            }

            return <div className="px-0.5 py-0.5 pl-1.5 text-gray-500">{override?.roleName ?? '-'}</div>;
          },
        }),
      );
    }

    if (canRemoveUsers) {
      cols.push(
        columnHelper.display({
          id: 'actions',
          header: 'Actions',
          enableSorting: false,
          cell: ({ row }) => (
            <div>
              {!row.original.isCreator && (
                <PermissionGate permission={Permission.MatterRemoveUsers} uiMode="disabled">
                  <Button size="sm" onClick={() => onRemoveUser(row.original.id)}>
                    <Icon className="lucide--x" />
                    Remove
                  </Button>
                </PermissionGate>
              )}
            </div>
          ),
        }),
      );
    }

    return cols;
  }, [
    overridesEnabled,
    canManageOverrides,
    canRemoveUsers,
    roles,
    uniqueRoles,
    hasSeatBasedUsers,
    onRoleOverrideChange,
    onRemoveUser,
  ]);

  const table = useReactTable({
    data: matterUsers,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getRowId: (row) => row.id,
    globalFilterFn: 'includesString',
    initialState: { pagination: { pageSize: 10 } },
    state: { sorting, globalFilter },
  });

  return (
    <ScrollableTable
      table={table}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      plural="users"
      additionalButtons={additionalButtons}
      className="-mx-8"
    />
  );
}

/** Helper to build MatterUserRow[] from the data sources */
export function buildMatterUserRows(
  matterUsers: User[],
  orgUserMap: Map<string, User>,
  overrideMap: Map<string, { id: string; roleId: string; roleName: string }>,
  currentUserId: string | undefined,
  matterCreatorId: string | undefined,
): MatterUserRow[] {
  return matterUsers.map((u) => {
    const orgUser = orgUserMap.get(u.id);
    const override = overrideMap.get(u.id);
    const isCreator = u.id === matterCreatorId;
    // Admin = the immutable Administrator system role, not any role that happens
    // to carry user:manage. Mirrors the backend (permission-override.service).
    const isAdmin = orgUser?.role?.name === ADMINISTRATOR_ROLE_NAME;
    return {
      id: u.id,
      email: u.email,
      globalRoleName: orgUser?.role?.name ?? 'Unknown',
      roleOverrideName: isAdmin ? ADMINISTRATOR_ROLE_NAME : (override?.roleName ?? ''),
      isCurrentUser: u.id === currentUserId,
      isCreator,
      isAdmin,
      override,
    };
  });
}
